"use client";
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function IconAnalyse({ color }: { color: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="10.5" cy="10.5" r="6.5" stroke={color} strokeWidth="2" />
            <path d="M15.5 15.5L21 21" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function IconIdentify({ color }: { color: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" />
            <circle cx="12" cy="12" r="4.5" stroke={color} strokeWidth="2" />
            <circle cx="12" cy="12" r="1.2" fill={color} />
        </svg>
    );
}
function IconHypothesise({ color }: { color: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 18h6M10 21h4" stroke={color} strokeWidth="2" strokeLinecap="round" />
            <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.45 1 1.1 1 1.9v.2h5v-.2c0-.8.4-1.45 1-1.9A6 6 0 0 0 12 3z" stroke={color} strokeWidth="2" strokeLinejoin="round" />
        </svg>
    );
}
function IconTest({ color }: { color: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 3h6M10 3v6.5L5.5 18a2 2 0 0 0 1.8 3h9.4a2 2 0 0 0 1.8-3L14 9.5V3" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 15h8" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function IconMeasure({ color }: { color: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M4 20V13M11 20V8M18 20v-6" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
        </svg>
    );
}
function IconImprove({ color }: { color: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M4 17l5-5 4 3 7-8" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15 6h5v5" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

const steps = [
    { num: '01', title: 'Analyse', desc: 'Study analytics, behaviour, conversion paths and available user feedback.', angle: -90, Icon: IconAnalyse },
    { num: '02', title: 'Identify', desc: 'Find friction points, drop-offs and potential opportunities.', angle: -30, Icon: IconIdentify },
    { num: '03', title: 'Hypothesise', desc: 'Develop specific hypotheses about what could improve the experience.', angle: 30, Icon: IconHypothesise },
    { num: '04', title: 'Test', desc: 'Run controlled experiments where appropriate.', angle: 90, Icon: IconTest },
    { num: '05', title: 'Measure', desc: 'Evaluate the impact against meaningful conversion metrics.', angle: 150, Icon: IconMeasure },
    { num: '06', title: 'Improve', desc: 'Apply learnings and continue testing the next opportunity.', angle: 210, Icon: IconImprove },
];

const STEP_MS = 2200;

function ProcessWheel({ active, setActive }: { active: number; setActive: (i: number) => void }) {
    const cur = steps[active];

    return (
        <div className="relative w-full max-w-[560px] aspect-square mx-auto">
            {/* rotating orbit rings */}
            <motion.svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                animate={{ rotate: 360 }}
                transition={{ duration: 70, repeat: Infinity, ease: 'linear' }}
            >
                <circle cx="50" cy="50" r="41" fill="none" stroke={PURPLE} strokeOpacity="0.2" strokeWidth="0.5" strokeDasharray="1 4.5" strokeLinecap="round" />
            </motion.svg>

            {/* center hub */}
            <div
                className="absolute rounded-[28px] flex flex-col items-center justify-center text-center px-6"
                style={{
                    left: '50%', top: '50%', width: 236, height: 236, marginLeft: -118, marginTop: -118,
                    background: CARD, border: '1px solid rgba(8,4,0,0.08)', boxShadow: '0 20px 50px -18px rgba(8,4,0,0.22)',
                }}
            >
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.3 }}
                        className="flex flex-col items-center gap-2"
                    >
                        <span
                            className="w-11 h-11 rounded-full flex items-center justify-center mb-1"
                            style={{ background: PURPLE }}
                        >
                            <cur.Icon color="#ffffff" />
                        </span>
                        <span className="text-[10px] font-mono" style={{ color: 'rgba(8,4,0,0.4)' }}>
                            {cur.num} / 06
                        </span>
                        <h3 className="text-lg font-bold leading-snug" style={{ fontFamily: CLASH, color: INK }}>
                            {cur.title}
                        </h3>
                        <p className="text-xs leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.55)' }}>
                            {cur.desc}
                        </p>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* rim nodes */}
            {steps.map((s, i) => {
                const rad = (s.angle * Math.PI) / 180;
                const rPct = 41;
                const left = 50 + rPct * Math.cos(rad);
                const top = 50 + rPct * Math.sin(rad);
                const isActive = i === active;
                return (
                    <button
                        key={s.title}
                        onClick={() => setActive(i)}
                        aria-label={`Show ${s.title}`}
                        className="absolute"
                        style={{ left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                        <motion.span
            initial={{ opacity: 0, scale: 0.5, backgroundColor: 'rgba(55,48,163,0.05)', borderColor: 'rgba(55,48,163,0.2)' }}
                            animate={{
                                opacity: 1,
                                backgroundColor: isActive ? LIME : 'rgba(55,48,163,0.05)',
                                borderColor: isActive ? LIME : 'rgba(55,48,163,0.2)',
                                scale: isActive ? 1.12 : 1,
                            }}
                            transition={{ duration: 0.35, delay: 0.1 + i * 0.06, ease: 'backOut' }}
                            className="flex flex-col items-center justify-center rounded-full"
                            style={{ width: 60, height: 60, border: '1.5px solid', boxShadow: isActive ? '0 0 20px rgba(206,255,26,0.5)' : '0 8px 20px -10px rgba(8,4,0,0.12)' }}
                        >
                            <span className="text-[9px] font-mono" style={{ color: isActive ? INK : 'rgba(8,4,0,0.35)' }}>
                                {s.num}
                            </span>
                            <span className="text-[9px] font-bold uppercase" style={{ fontFamily: CLASH, color: isActive ? INK : 'rgba(8,4,0,0.55)' }}>
                                {s.title}
                            </span>
                        </motion.span>
                    </button>
                );
            })}
        </div>
    );
}

function StepList({ active, onSelect }: { active: number; onSelect: (i: number) => void }) {
    return (
        <div className="flex flex-col" style={{ borderTop: '1px solid rgba(8,4,0,0.08)' }}>
            {steps.map((s, i) => {
                const isActive = i === active;
                return (
                    <button
                        key={s.title}
                        onClick={() => onSelect(i)}
                        className="flex items-center gap-4 py-4 text-left transition-colors duration-300"
                        style={{ borderBottom: '1px solid rgba(8,4,0,0.08)' }}
                    >
                        <span
                            className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 transition-colors duration-300"
                            style={{ fontFamily: CLASH, background: isActive ? PURPLE : 'rgba(55,48,163,0.06)', color: isActive ? '#ffffff' : PURPLE, border: isActive ? 'none' : '1px solid rgba(55,48,163,0.16)' }}
                        >
                            {s.num}
                        </span>
                        <span
                            className="text-base md:text-lg font-bold transition-colors duration-300"
                            style={{ fontFamily: CLASH, color: isActive ? INK : 'rgba(8,4,0,0.35)' }}
                        >
                            {s.title}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export default function CROProcess() {
    const [active, setActive] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setActive((prev) => (prev + 1) % steps.length);
        }, STEP_MS);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    return (
        <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1300px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                <div className="lg:col-span-5 flex flex-col gap-8">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.25em] uppercase"
                            style={{ color: PURPLE, fontFamily: CLASH }}
                        >
                            How We Work
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            Our <span style={{ color: PURPLE }}>CRO Process</span>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            className="text-base leading-relaxed"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)' }}
                        >
                            A continuous loop, not a one-off project — each cycle feeds the next.
                        </motion.p>
                    </div>

                    <StepList active={active} onSelect={setActive} />
                </div>

                <div className="lg:col-span-7">
                    <ProcessWheel active={active} setActive={setActive} />
                </div>
            </div>
        </section>
    );
}
